import { AlertTriangle, BookOpen, DatabaseZap, RefreshCw, SearchX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { apiRequest } from "../lib/api";
import { APP_ROUTES } from "../routes";

type AgentGapType = "knowledge" | "database";

interface KnowledgeGapEntry {
  id: string;
  conversationId: string;
  messageId: string;
  agentId: string;
  agentName: string;
  userName: string;
  userIdentity: string;
  gapType: AgentGapType;
  userQuestion: string;
  query: string;
  reason: string;
  status: "open" | "resolved" | "ignored";
  summary: string;
  metadata: Record<string, unknown> | null;
  createdAt: number;
}

interface KnowledgeGapsResponse {
  gaps: KnowledgeGapEntry[];
  totalOpen: number;
}

const formatTime = (timestamp: number): string =>
  new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const gapTypeLabel = (gapType: AgentGapType): string =>
  gapType === "database" ? "Database gap" : "Knowledge gap";

const reasonLabel = (reason: string): string => {
  if (reason === "knowledge_search_error") {
    return "Retrieval error";
  }
  if (reason === "no_database_answer") {
    return "No database answer";
  }
  return "No evidence found";
};

const readSearchedQueries = (metadata: Record<string, unknown> | null): string[] => {
  const value = metadata?.searchedQueries;
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && Boolean(item.trim()))
    : [];
};

export function KnowledgeGapsPage() {
  const [payload, setPayload] = useState<KnowledgeGapsResponse>({
    gaps: [],
    totalOpen: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGaps = async (mode: "initial" | "refresh" = "initial") => {
    if (mode === "initial") {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      const nextPayload = await apiRequest<KnowledgeGapsResponse>(
        "/api/querygen/knowledge-gaps?limit=75",
      );
      setPayload(nextPayload);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load knowledge gaps",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void loadGaps();
  }, []);

  const stats = useMemo(() => {
    const database = payload.gaps.filter((gap) => gap.gapType === "database").length;
    const knowledge = payload.gaps.length - database;
    const agentCount = new Set(payload.gaps.map((gap) => gap.agentId)).size;
    return { database, knowledge, agentCount };
  }, [payload.gaps]);

  return (
    <div className="min-h-full bg-slate-50">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6 lg:px-10">
        <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              <SearchX className="h-4 w-4" />
              Agent review
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">
              Knowledge gaps
            </h1>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
              Logged questions the agent could not ground in approved knowledge or database data.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => void loadGaps("refresh")}
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button asChild>
              <Link to={APP_ROUTES.knowledgebase}>
                <BookOpen />
                Update knowledgebase
              </Link>
            </Button>
          </div>
        </header>

        <section className="grid gap-3 md:grid-cols-4">
          <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
              Open gaps
            </div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">
              {payload.totalOpen}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
              Knowledge
            </div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">
              {stats.knowledge}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
              Database
            </div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">
              {stats.database}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
              Agents affected
            </div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">
              {stats.agentCount}
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-950">Recent gaps</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Captured as separate events when agents cannot ground an answer.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3 p-4">
              {[...Array(5)].map((_, index) => (
                <div
                  key={index}
                  className="h-24 animate-pulse rounded-md bg-slate-100"
                />
              ))}
            </div>
          ) : payload.gaps.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                <DatabaseZap className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-sm font-semibold text-slate-950">
                No knowledge gaps found
              </h3>
              <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
                No open gap events have been logged yet.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {payload.gaps.map((gap) => (
                <article key={gap.id} className="grid gap-4 px-4 py-4 lg:grid-cols-[1fr_220px]">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          gap.gapType === "database"
                            ? "border-sky-200 bg-sky-50 text-sky-700"
                            : gap.reason === "knowledge_search_error"
                              ? "border-red-200 bg-red-50 text-red-700"
                              : "border-amber-200 bg-amber-50 text-amber-800"
                        }
                      >
                        {gapTypeLabel(gap.gapType)}
                      </Badge>
                      <span className="text-xs text-slate-500">{reasonLabel(gap.reason)}</span>
                      <span className="text-xs text-slate-400">/</span>
                      <span className="text-xs text-slate-500">{formatTime(gap.createdAt)}</span>
                    </div>

                    <div className="mt-3 rounded-md bg-slate-50 px-3 py-2">
                      <div className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                        Search query
                      </div>
                      <div className="mt-1 text-sm font-medium text-slate-950">
                        {gap.query || "No query captured"}
                      </div>
                      {readSearchedQueries(gap.metadata).length > 1 ? (
                        <div className="mt-2 text-xs leading-5 text-slate-500">
                          Also tried: {readSearchedQueries(gap.metadata).slice(0, -1).join(", ")}
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                          User asked
                        </div>
                        <p className="mt-1 line-clamp-3 text-sm leading-6 text-slate-700">
                          {gap.userQuestion || "No user message found before this response."}
                        </p>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                          Gap summary
                        </div>
                        <p className="mt-1 line-clamp-3 text-sm leading-6 text-slate-700">
                          {gap.summary}
                        </p>
                      </div>
                    </div>

                    {gap.reason === "knowledge_search_error" ? (
                      <div className="mt-3 flex items-start gap-2 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        Knowledge retrieval failed while handling this question.
                      </div>
                    ) : null}
                  </div>

                  <aside className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
                      Context
                    </div>
                    <div className="mt-2 space-y-2 text-sm">
                      <div>
                        <div className="text-xs text-slate-500">Agent</div>
                        <div className="truncate font-medium text-slate-900">{gap.agentName}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Conversation user</div>
                        <div className="truncate text-slate-700">
                          {gap.userName || gap.userIdentity}
                        </div>
                      </div>
                    </div>
                    <Button asChild variant="outline" size="sm" className="mt-4 w-full">
                      <Link to={APP_ROUTES.knowledgebase}>Add source</Link>
                    </Button>
                  </aside>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
